import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Key, CheckCircle, AlertCircle } from 'lucide-react';

const MFASetup = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [setupStatus, setSetupStatus] = useState('loading'); // loading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Check for Auth0 MFA enrollment parameters
    const ticket = searchParams.get('ticket');
    const user_id = searchParams.get('user_id');
    
    if (ticket) {
      // Auth0 Guardian MFA enrollment ticket
      handleAuth0MfaEnrollment(ticket);
    } else if (user_id) {
      // Custom MFA setup flow (fallback)
      setSetupStatus('error');
      setErrorMessage('Please use the MFA enrollment link sent to your email to complete setup.');
    } else {
      setSetupStatus('error');
      setErrorMessage('Invalid MFA setup link. Please contact your administrator.');
    }
  }, [searchParams]);

  const handleAuth0MfaEnrollment = async (ticket) => {
    try {
      setIsLoading(true);
      setSetupStatus('loading');
      
      // For Auth0 Guardian enrollment, we redirect to Auth0's enrollment page
      // The ticket parameter contains the enrollment URL
      const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN || 'your-domain.auth0.com';
      const enrollmentUrl = `https://${auth0Domain}/guardian/enrollments/ticket?ticket=${ticket}`;
      
      console.log('Redirecting to Auth0 MFA enrollment:', enrollmentUrl);
      
      // Redirect to Auth0's MFA enrollment page
      window.location.href = enrollmentUrl;
      
    } catch (error) {
      console.error('MFA enrollment error:', error);
      setSetupStatus('error');
      setErrorMessage('Failed to start MFA enrollment. Please try again or contact support.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const handleRetry = () => {
    window.location.reload();
  };

  if (setupStatus === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{
        background: 'linear-gradient(135deg, #207CE5 0%, #008B8B 100%)'
      }}>
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-white p-3 rounded-full shadow-lg">
                <img 
                  src="/Vital-logo.png" 
                  alt="VITAL AUDIO Logo" 
                  className="h-8 w-8 object-contain"
                />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Sonometrik</h1>
          </div>

          {/* Loading State */}
          <div className="glass-effect rounded-2xl p-8 shadow-xl text-center">
            <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-blue-100 mb-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Setting up MFA</h2>
            <p className="text-white/80 text-sm">Redirecting to MFA enrollment...</p>
          </div>
        </div>
      </div>
    );
  }

  if (setupStatus === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{
        background: 'linear-gradient(135deg, #207CE5 0%, #008B8B 100%)'
      }}>
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-white p-3 rounded-full shadow-lg">
                <img 
                  src="/Vital-logo.png" 
                  alt="VITAL AUDIO Logo" 
                  className="h-8 w-8 object-contain"
                />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Sonometrik</h1>
          </div>

          {/* Success State */}
          <div className="glass-effect rounded-2xl p-8 shadow-xl text-center">
            <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-green-100 mb-6">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">MFA Setup Complete!</h2>
            <p className="text-white/80 text-sm mb-6">
              Your Multi-Factor Authentication has been successfully set up. You can now log in with enhanced security.
            </p>
            <button
              onClick={handleBackToLogin}
              className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Continue to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{
      background: 'linear-gradient(135deg, #207CE5 0%, #008B8B 100%)'
    }}>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-white p-3 rounded-full shadow-lg">
              <img 
                src="/Vital-logo.png" 
                alt="VITAL AUDIO Logo" 
                className="h-8 w-8 object-contain"
              />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Sonometrik</h1>
        </div>

        {/* Error State */}
        <div className="glass-effect rounded-2xl p-8 shadow-xl text-center">
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-red-100 mb-6">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">MFA Setup Error</h2>
          <p className="text-white/80 text-sm mb-6">
            {errorMessage}
          </p>
          <div className="flex flex-col space-y-3">
            <button
              onClick={handleRetry}
              className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={handleBackToLogin}
              className="w-full py-3 px-4 border border-white/20 text-white rounded-lg hover:bg-white/10 transition-colors"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MFASetup;
